import React, { useEffect, useState } from 'react';
import './UserQR.css';

const UserQR = () => {
  const [userData, setUserData] = useState(null);
  const [userPhotoBase64, setUserPhotoBase64] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState({});

  // Fetch user photo as base64 from API endpoint
  const fetchUserPhotoFromAPI = async (userId, token, isPublic = false) => {
    try {
      console.log(`🔄 Fetching photo from ${isPublic ? 'PUBLIC' : 'AUTHENTICATED'} API...`);
      
      const apiUrl = isPublic
        ? `https://api.asterickx.com/api/PublicUser/${userId}/photo`
        : `https://api.asterickx.com/api/Members/${userId}/photo`;
      
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (token && !isPublic) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.photoData) {
          console.log(`✅ Photo loaded from ${isPublic ? 'PUBLIC' : 'AUTHENTICATED'} API`);
          setUserPhotoBase64(data.photoData);
          return data.photoData;
        }
      } else {
        console.warn(`⚠️ Photo API failed (${isPublic ? 'public' : 'auth'}):`, response.status);
      }
    } catch (error) {
      console.error(`Error fetching photo from ${isPublic ? 'public' : 'auth'} API:`, error);
    }
    return null;
  };

  // Helper functions
  const getGenderName = (genderId) => {
    const genders = {
      "293BC0D5-FF50-4626-830D-0F9CC08A31E1": "Female",
      "C7EABC7E-9A9D-4F5B-B5DE-A78BF4BBAAC2": "Male",
      "9D16E81C-DFD1-4773-BEAA-AA6146C4C2E5": "Other"
    };
    if (!genderId) return "Not specified";
    return genders[genderId.toString().toUpperCase().trim()] || "Not specified";
  };

  const getFoodPreferenceName = (foodPrefId) => {
    const foodPrefs = {
      "AA6AE8A5-E43E-474E-952A-65850AEB790C": "Non-Vegetarian",
      "E24A36F1-1BAC-40F5-A3D4-73596FAEA3C4": "Vegetarian",
      "45DD9A6A-3E33-4703-BD2C-F0C5C0966AD2": "Jain"
    };
    if (!foodPrefId) return "Not specified";
    return foodPrefs[foodPrefId.toString().toUpperCase().trim()] || "Not specified";
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric'
      });
    } catch { return 'N/A'; }
  };

  const getMobileNumber = (userData) => {
    const fields = ['mobileNumber', 'MobileNumber', 'mobile', 'phone', 'phoneNumber', 'Mobile', 'Phone'];
    for (const field of fields) {
      if (userData[field]) return userData[field];
    }
    return 'Not available';
  };

  const getUserDisplayName = (userData) => {
    const firstName = userData.firstName || userData.FirstName || '';
    const lastName = userData.lastName || userData.LastName || '';
    if (firstName || lastName) return `${firstName} ${lastName}`.trim();
    return userData.name || userData.fullName || userData.userName || 'Unknown User';
  };

  const getUserEmail = (userData) => {
    return userData.email || userData.Email || 'Not available';
  };

  const getUserState = (userData) => {
    return userData.state || userData.State || 'Not available';
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const urlParams = new URLSearchParams(window.location.search);
        const userId = urlParams.get('id');

        const debug = {
          userId,
          hasToken: !!(localStorage.getItem('Token') || localStorage.getItem('token')),
          url: window.location.href,
          userAgent: navigator.userAgent,
          isMobile: /Mobile|Android|iPhone/i.test(navigator.userAgent)
        };
        
        console.log('=== USERQR FETCH (Mobile Support) ===');
        console.log('Debug:', debug);
        setDebugInfo(debug);

        if (!userId) {
          setError('No user ID in URL');
          setLoading(false);
          return;
        }

        let userData = null;
        const token = localStorage.getItem('Token') || localStorage.getItem('token');
        
        // PRIORITY 1: Try PUBLIC endpoint (works without login - for mobile)
        try {
          console.log('📱 Trying PUBLIC endpoint (no auth required)...');
          const publicApiUrl = `https://api.asterickx.com/api/PublicUser/${userId}`;
          const publicResponse = await fetch(publicApiUrl, {
            headers: { 'Content-Type': 'application/json' }
          });

          debug.publicResponse = { status: publicResponse.status, ok: publicResponse.ok };

          if (publicResponse.ok) {
            userData = await publicResponse.json();
            console.log('✅ PUBLIC API SUCCESS (works on mobile!)');
            console.log('Data:', userData);
            debug.source = 'PublicUser API (no auth)';
            
            // Fetch photo from public endpoint
            await fetchUserPhotoFromAPI(userId, null, true);
          } else {
            console.warn('❌ Public API failed:', publicResponse.status);
            debug.publicError = `Status ${publicResponse.status}`;
          }
        } catch (err) {
          console.error('❌ Public API error:', err);
          debug.publicError = err.message;
        }

        // PRIORITY 2: Try authenticated endpoint (if has token)
        if (!userData && token) {
          try {
            console.log('🔐 Trying AUTHENTICATED endpoint (with token)...');
            const authApiUrl = `https://api.asterickx.com/api/Members/${userId}`;
            const authResponse = await fetch(authApiUrl, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            debug.authResponse = { status: authResponse.status, ok: authResponse.ok };

            if (authResponse.ok) {
              userData = await authResponse.json();
              console.log('✅ AUTHENTICATED API SUCCESS');
              console.log('Data:', userData);
              debug.source = 'Members API (with auth)';
              
              // Fetch photo from authenticated endpoint
              await fetchUserPhotoFromAPI(userId, token, false);
            } else {
              console.warn('❌ Authenticated API failed:', authResponse.status);
              debug.authError = `Status ${authResponse.status}`;
            }
          } catch (err) {
            console.error('❌ Authenticated API error:', err);
            debug.authError = err.message;
          }
        }

        if (!userData) {
          const errorMsg = debug.isMobile 
            ? 'Unable to load user data. Please ensure the public API endpoint is configured on the backend.'
            : 'Unable to fetch user data. Invalid user ID or server error.';
          setError(errorMsg);
          debug.result = 'Failed';
          debug.hint = 'Add PublicUserController to backend to enable mobile scanning';
        } else {
          setUserData(userData);
          debug.result = 'Success';
        }

        setDebugInfo(debug);

      } catch (error) {
        console.error('Fetch error:', error);
        setError('Unexpected error: ' + error.message);
        setDebugInfo(prev => ({ ...prev, error: error.message }));
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleGoBack = () => {
    window.history.length > 1 ? window.history.back() : window.location.href = '/';
  };

  const handleScanAnother = () => {
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 flex items-center justify-center p-6">
        <div className="text-center bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-12 border border-rose-100">
          <div className="w-24 h-24 border-4 border-rose-200 border-t-rose-500 rounded-full animate-spin mx-auto mb-8"></div>
          <h3 className="text-gray-800 text-2xl font-bold mb-4">Loading User Profile</h3>
          <p className="text-gray-600 text-lg">Fetching user details...</p>
        </div>
      </div>
    );
  }

  if (error && !userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-100 via-rose-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-10 max-w-lg w-full text-center border border-red-200">
          <div className="text-red-400 text-8xl mb-8">⚠️</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Unable to Load Profile</h2>
          <p className="text-gray-600 mb-4 text-lg">{error}</p>
          
          {debugInfo.isMobile && (
            <div className="mb-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <p className="text-yellow-800 text-sm">
                📱 <strong>Mobile Device Detected</strong><br/>
                Backend needs public API endpoint for mobile QR scanning.
              </p>
            </div>
          )}
          
          <details className="mb-8 text-left bg-red-50 rounded-2xl p-6 border border-red-200">
            <summary className="cursor-pointer text-red-600 font-semibold">Technical Details</summary>
            <pre className="mt-4 text-sm bg-white p-4 rounded-xl overflow-auto max-h-48 border font-mono">
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </details>

          <div className="space-y-4">
            <button onClick={handleGoBack} className="w-full bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold py-4 px-8 rounded-2xl transition-all">
              ← Go Back
            </button>
            <button onClick={handleScanAnother} className="w-full bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white font-bold py-4 px-8 rounded-2xl transition-all">
              🔄 Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 flex items-center justify-center p-6">
        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-10 max-w-md w-full text-center border border-rose-200">
          <div className="text-gray-400 text-8xl mb-8">👤</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">No User Data</h2>
          <p className="text-gray-600 mb-8">Unable to retrieve user information</p>
          <button onClick={handleGoBack} className="w-full bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold py-4 px-8 rounded-2xl">
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  const displayName = getUserDisplayName(userData);
  const email = getUserEmail(userData);
  const mobile = getMobileNumber(userData);
  const state = getUserState(userData);
  const birthdate = userData.birthdate || userData.Birthdate || userData.dateOfBirth || userData.DateOfBirth;
  const genderId = userData.genderId || userData.GenderId;
  const foodPrefId = userData.foodPreferenceId || userData.FoodPreferenceId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-rose-50 to-pink-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Success indicator for debugging */}
        {debugInfo.source && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-4 mb-6 text-center">
            <p className="text-green-800 text-sm">
              ✅ Data loaded from: <strong>{debugInfo.source}</strong>
              {debugInfo.isMobile && " (Mobile device)"}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-10 mb-8 text-center border border-rose-200 shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-rose-100/50 to-red-100/50"></div>
          <div className="relative z-10">
            <div className="inline-block bg-gradient-to-r from-rose-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              QR SCAN RESULT
            </div>
            <h1 className="text-5xl font-bold bg-gradient-to-r from-rose-600 to-red-700 bg-clip-text text-transparent mb-4">
              User Profile
            </h1>
            <p className="text-gray-700 text-xl font-medium">Digital Identity Verification</p>
          </div>
        </div>

        {/* User Card */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl overflow-hidden mb-8 border border-rose-200">
          {/* Profile Header */}
          <div className="bg-gradient-to-r from-rose-400 via-red-400 to-pink-500 p-10 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-20 translate-x-20"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-16 -translate-x-16"></div>
            
            <div className="relative z-10">
              {/* Profile Photo */}
              <div className="w-40 h-40 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-8 relative overflow-hidden border-4 border-white/40 shadow-2xl">
                {userPhotoBase64 ? (
                  <img
                    src={userPhotoBase64}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover"
                    onLoad={() => console.log('✅ Photo displayed in UserQR')}
                    onError={() => console.error('❌ Photo display failed in UserQR')}
                  />
                ) : (
                  <span className="text-6xl text-white">👤</span>
                )}
              </div>
              
              <h2 className="text-4xl font-bold text-white mb-4 drop-shadow-lg">{displayName}</h2>
              
              <div className="inline-block bg-white/25 backdrop-blur-sm rounded-full px-8 py-3 border border-white/30">
                <p className="text-white font-semibold text-xl">Member</p>
              </div>
            </div>
          </div>

          {/* Details Grid */}
          <div className="p-10">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {/* User ID */}
              <div className="bg-gradient-to-br from-white to-red-50 rounded-2xl p-6 border border-red-100 hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-rose-500 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">🆔</div>
                  <span className="font-bold text-gray-800 text-lg">User ID</span>
                </div>
                <p className="text-gray-900 font-mono text-sm bg-red-50 rounded-xl p-3 border border-red-200 break-all">{userData.id}</p>
              </div>

              {/* Email */}
              <div className="bg-gradient-to-br from-white to-rose-50 rounded-2xl p-6 border border-rose-100 hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">✉️</div>
                  <span className="font-bold text-gray-800 text-lg">Email</span>
                </div>
                <p className="text-gray-900 break-all">{email}</p>
              </div>

              {/* Mobile */}
              <div className="bg-gradient-to-br from-white to-red-50 rounded-2xl p-6 border border-red-100 hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-rose-600 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">📱</div>
                  <span className="font-bold text-gray-800 text-lg">Mobile</span>
                </div>
                <p className="text-gray-900 font-semibold text-lg">{mobile}</p>
              </div>

              {/* Gender */}
              {genderId && (
                <div className="bg-gradient-to-br from-white to-pink-50 rounded-2xl p-6 border border-pink-100 hover:shadow-xl transition-all">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-400 to-rose-500 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">👤</div>
                    <span className="font-bold text-gray-800 text-lg">Gender</span>
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{getGenderName(genderId)}</p>
                </div>
              )}

              {/* Birth Date */}
              {birthdate && (
                <div className="bg-gradient-to-br from-white to-rose-50 rounded-2xl p-6 border border-rose-100 hover:shadow-xl transition-all">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-red-600 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">🎂</div>
                    <span className="font-bold text-gray-800 text-lg">Birth Date</span>
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{formatDate(birthdate)}</p>
                </div>
              )}

              {/* State */}
              <div className="bg-gradient-to-br from-white to-red-50 rounded-2xl p-6 border border-red-100 hover:shadow-xl transition-all">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-red-400 to-pink-500 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">🗺️</div>
                  <span className="font-bold text-gray-800 text-lg">State</span>
                </div>
                <p className="text-gray-900 font-semibold text-lg">{state}</p>
              </div>

              {/* Food Preference */}
              {foodPrefId && (
                <div className="bg-gradient-to-br from-white to-rose-50 rounded-2xl p-6 border border-rose-100 hover:shadow-xl transition-all">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-red-500 rounded-xl flex items-center justify-center text-white text-xl mr-4 shadow-lg">🍽️</div>
                    <span className="font-bold text-gray-800 text-lg">Food Preference</span>
                  </div>
                  <p className="text-gray-900 font-semibold text-lg">{getFoodPreferenceName(foodPrefId)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl p-10 border border-rose-200">
          <button onClick={handleScanAnother} className="w-full bg-gradient-to-r from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 text-white font-bold py-5 px-10 rounded-2xl transition-all transform hover:scale-105 flex items-center justify-center shadow-xl">
            <span className="text-2xl mr-4">🔄</span>
            <span className="text-xl">Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserQR;